// =============================================================================
// US-PLATFORM-02 -- i18n scaffolding
// =============================================================================
//
// A small custom primitive -- no external i18n library -- that provides the
// `t(key, params?)` interface every screen will eventually use. Single
// locale (en-US) bundled at compile time. The shape mirrors what
// @solid-primitives/i18n or i18next would provide for a single locale,
// so swapping to a real lib later (when we add a second locale) is a
// signature-compatible change.
//
// Why no external dep:
//   * The MVP target is "infrastructure ready, en-US shipped, swappable
//     for a real lib post-MVP". A 30-line primitive does that without
//     adding a dep, a build step, or a new bundle weight.
//   * Solid's reactivity isn't needed here -- locale is fixed at en-US
//     for MVP. When a second locale is added, we'll switch to a real
//     lib that handles locale state via Solid signals.
//
// Convention: every new user-visible string MUST go through `t()` with
// a key in the en-US bundle. See docs/CONVENTIONS.md section 19.

import enUS from './locales/en-US.json';

/** Currently fixed at en-US. When a second locale ships post-MVP, this
 *  becomes a Solid signal driven by the user's preference. */
const ACTIVE_LOCALE = 'en-US' as const;

const messages: Record<string, Record<string, string>> = {
  'en-US': enUS,
};

/** Type-safe key set derived from the en-US bundle. Adding a key to
 *  the JSON bundle automatically widens this type. Removing a used
 *  key fails typecheck. */
export type I18nKey = keyof typeof enUS;

/** Format a translation key against the active locale.
 *
 *  Supports `{name}`-style interpolation:
 *      t('login.subtitle', { name: 'Sarah' })
 *      -> "Welcome back, Sarah!"
 *
 *  Missing keys return the key unchanged and console.warn so the
 *  developer notices in dev. The key-on-miss behaviour is
 *  deliberately visible -- silently rendering empty would hide bugs.
 */
export function t(key: I18nKey, params?: Record<string, string | number>): string {
  const bundle = messages[ACTIVE_LOCALE];
  if (!bundle) {
    // Should be impossible -- ACTIVE_LOCALE is a constant and en-US is
    // bundled at compile time. Defensive return so the strict type
    // checker is happy.
    return key;
  }
  const template = bundle[key];
  if (template === undefined) {
    if (typeof console !== 'undefined') {
      console.warn(`[i18n] missing key '${key}' in locale '${ACTIVE_LOCALE}'`);
    }
    return key;
  }
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

/** Returns the active locale tag. Useful for `<html lang>` and date/
 *  number formatters that need the BCP-47 locale string. */
export function activeLocale(): string {
  return ACTIVE_LOCALE;
}
